// tracking_scripts/tag_manager/consent.ts

import { getConsentStatus } from '../../consent/consentManager';

/**
 * Class to manage consent settings for Google Tag Manager.
 * Handles the initialization of consent settings based on user preferences and
 * updates Google Tag Manager (GTM) accordingly, ensuring that tags are fired
 * according to the user's consent choices.
 */
export class TagManagerConsent {
	constructor() {
		this.initialiseConsent();
	}

	/**
	 * Initializes consent settings and updates the GTM dataLayer.
	 * Sets default consent settings, determines user preferences using the `checkConsent` function,
	 * and updates the dataLayer with these preferences, then initializes GTM.
	 */
	private initialiseConsent(): void {
		window.dataLayer = window.dataLayer || [];

		this.setDefaultConsent();
	}

	/**
	 * Sets default consent values for GTM.
	 * Pushes initial consent settings to the GTM dataLayer to establish a baseline before user preferences are applied.
	 */
	private setDefaultConsent(): void {
		this.gtag('consent', 'default', {
			ad_user_data: 'denied',
			ad_personalization: 'denied',
			ad_storage: 'denied',
			personalization_storage: 'denied',
			analytics_storage: 'denied',
			functionality_storage: 'denied',
			security_storage: 'denied',
		});
	}

	/**
	 * Updates consent settings in GTM based on user preferences.
	 * Determines user consent preferences and pushes the updated settings to the GTM dataLayer
	 * to ensure that GTM tags adhere to the user's consent status.
	 */
	public updateConsent(): void {
		// Determine the consent status for each category based on user choices
		const marketingConsentGranted = getConsentStatus('marketing') ? 'granted' : 'denied';
		const functionalConsentGranted = getConsentStatus('functional') ? 'granted' : 'denied';

		// Push updated consent settings to GTM
		this.gtag('consent', 'update', {
			ad_storage: marketingConsentGranted,
			ad_user_data: marketingConsentGranted,
			ad_personalization: marketingConsentGranted,
			personalization_storage: marketingConsentGranted,
			analytics_storage: functionalConsentGranted,
			functionality_storage: functionalConsentGranted,
			security_storage: 'granted',
		});
	}

	/**
	 * Pushes data to the GTM `dataLayer`.
	 * This function serves as a wrapper around the `dataLayer.push` method to standardize
	 * the data format sent to Google Tag Manager (GTM). It handles both single-object
	 * and multiple-argument formats for different types of GTM events and configurations.
	 *
	 * It uses the `arguments` object to directly push data to the `dataLayer` to preserve
	 * the original format and types of the arguments passed.
	 * @param {...any} args
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public gtag(...args: any[]): void {
		window.dataLayer.push(arguments);
	}
}

// Export the class
export default TagManagerConsent;
